import { useState, useEffect, useMemo } from "react";
import { Button } from "./ui/button";
import { UserAvatar } from "./UserAvatar";
import { Link2, Send, X, Loader2, Check, Image, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "../i18n";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';

export function ShareModal({ itemId, itemTitle, isOpen, onClose, onCreateImage }) {
  const { t } = useTranslation();
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null);
  const [sent, setSent] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchFollowing();
      setSent(new Set());
      setSearchQuery("");
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return following;
    const q = searchQuery.toLowerCase().trim();
    return following.filter((u) => u.username?.toLowerCase().includes(q));
  }, [following, searchQuery]);

  const fetchFollowing = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my/following`);
      setFollowing(res.data.users || []);
    } catch { setFollowing([]); }
    finally { setLoading(false); }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(t.share.linkCopied);
  };

  const handleRecommend = async (receiverUserId) => {
    setSending(receiverUserId);
    try {
      await axios.post(`${API}/recommendations`, {
        receiver_user_id: receiverUserId,
        item_id: itemId,
      });
      setSent(prev => new Set([...prev, receiverUserId]));
      toast.success(t.share.recommendationSent);
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al enviar";
      if (err.response?.status === 409) {
        setSent(prev => new Set([...prev, receiverUserId]));
      }
      toast.error(msg);
    } finally { setSending(null); }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="share-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.2 }}
          className="bg-background border border-border rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="share-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-semibold text-base">{t.share.title}</h3>
            <Button variant="ghost" size="icon" className="w-8 h-8" onClick={onClose} data-testid="share-modal-close">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Actions */}
          <div className="p-4 space-y-2 border-b border-border">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-12"
              onClick={handleCopyUrl}
              data-testid="share-copy-url"
            >
              <Link2 className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium">{t.share.copyLink}</span>
            </Button>

            {onCreateImage && (
              <Button
                variant="outline"
                className="w-full justify-start gap-3 h-12"
                onClick={() => { onClose(); setTimeout(() => onCreateImage(), 150); }}
                data-testid="share-create-image"
              >
                <Image className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium">{t.share.createImage}</span>
              </Button>
            )}
          </div>

          {/* Friend list */}
          <div className="flex-1 overflow-y-auto p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3 font-medium">{t.share.recommendFriend}</p>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            ) : following.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                {t.share.notFollowingAnyone}
              </p>
            ) : (
              <>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t.share.searchUser}
                    className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    data-testid="share-search-input"
                  />
                </div>
                {filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6" data-testid="share-search-empty">
                    {t.share.noUsersFound}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {filtered.map((u) => {
                  const isSent = sent.has(u.user_id);
                  const isSending = sending === u.user_id;
                  return (
                    <button
                      key={u.user_id}
                      onClick={() => !isSent && !isSending && handleRecommend(u.user_id)}
                      disabled={isSent || isSending}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-60"
                      data-testid={`share-user-${u.user_id}`}
                    >
                      <UserAvatar user={u} size="sm" />
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium truncate">{u.username}</p>
                        <p className="text-xs text-muted-foreground truncate">@{u.username}</p>
                      </div>
                      {isSending ? (
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      ) : isSent ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Send className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                  );
                })}
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
